import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Container,
  Divider,
  HStack,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import {
  CurrencyIcon,
  DocumentTextOutlineIcon,
  FlagOutlineIcon,
  FlaskIcon,
  LigthIcon,
  ListIcon,
  TempIcon,
  UserCircle,
} from "../components/customIcons";
import React, { FC, useEffect, useRef, useState } from "react";

import Carousel from "../components/molecules/Carousel";
import { Helmet } from "react-helmet";
import RegularLayout from "../components/layouts/RegularLayout";
import RelatedProducts from "../components/molecules/RelatedProduct";
import { SEO } from "../components/molecules/Seo";
import SingleProductDetails from "../components/organisms/SingleProductDetails";
import { StaticImage } from "gatsby-plugin-image";
import SubstituteProducts from "../components/organisms/SubstituteProducts";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import useAuth from "../components/useAuth";
import PromoBanner from "../components/molecules/PromoBanner/PromoBanner";

type SingleProductPageType = { productTabs?: any };

const Div = styled.div`
  ul li {
    margin-top: 4px;
    margin-left: 18px;
  }
  ol {
    margin-left: 24px;
  }
`;

const SingleProductPage: FC<SingleProductPageType> = ({
  data,

  location,
}: any) => {
  const { loggedIn, isUnlicensedUser, user } = useAuth();

  const {
    wpSimpleProduct: {
      name,
      databaseId,
      salePrice,
      price,
      regularPrice,
      shortDescription,
      productLeaflet,
    },
    wpSimpleProduct: { image },

    wpSimpleProduct: {
      productCategories: { catName },
    },
    wpSimpleProduct: {
      productTags: { brandName },
      attributes,
      tierPrice,
      productTabs,
      stockStatus,
    },

    wpSimpleProduct: {
      related: { relatedProducts },
      upsell: { upsellProducts },
      crossSellProducts: { bundles },
      productSubstitute: { substitute },
    },
  } = data;

  const productName = data.wpSimpleProduct.name;
  const productSlug = data.wpSimpleProduct.slug;
  const categoryName = data.wpSimpleProduct.productCategories?.catName[0]?.name;
  const categorySlug = data.wpSimpleProduct.productCategories?.catName[0]?.slug;
  const productOnPromotion = data.wpSimpleProduct.isOnPromotion?.isonpromotion;
  const hasAdditionalPromoTags =
    data.wpSimpleProduct.isOnPromotion?.additionalTag;
  const pdfSlug = productLeaflet?.pdf?.mediaItemUrl;
  const bundle = data.wpSimpleProduct.crossSellProducts;

  const allowedCategories =
    data.wpSimpleProduct.productCategories.catName.filter(
      (category: { name: string }) => {
        return (
          category.name !== "Best Selling Product" &&
          category.name !== "Related Product"
        );
      },
    );
  const mainCategory = allowedCategories.find(
    (category: any) => !category.wpParent,
  );
  const subCategories = allowedCategories.filter(
    (category: any) => category.wpParent,
  );

  const productSchema = {
    "@context": "http://schema.org",
    "@type": "Product",
    name: productName,
    description: shortDescription,
    sku: databaseId,
    url: location.href,
    image: image?.sourceUrl,
    offers: {
      "@type": "Offer",
      price: price ? parseFloat(price?.replace("$", "")) : undefined,
      priceCurrency: "USD",
      availability: stockStatus,
    },
    brand: {
      "@type": "Brand",
      name: brandName[0]?.name || "",
    },
  };

  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "All products", link: "/shop/" },
    { label: mainCategory.name, link: `/${mainCategory.slug}/` },
    ...subCategories.map((category: { name: string; slug: string }) => ({
      label: category.name,
      link: `/${category.slug}/`,
    })),
    { label: `${productName}`, link: `/product/${productSlug}/` },
  ];

  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLInputElement | null>(null);
  const [expandedAboutProduct, setExpandedAboutProduct] = useState(false);

  useEffect(() => {
    const checkIfTextIsTruncated = () => {
      if (expandedAboutProduct) {
        setIsTruncated(true);
      } else if (textRef.current) {
        const isTextTruncated =
          textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsTruncated(isTextTruncated);
      }
    };

    checkIfTextIsTruncated();
    // Add an event listener for window resize in case the content dynamically changes size
    window.addEventListener("resize", checkIfTextIsTruncated);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", checkIfTextIsTruncated);
    };
  }, [shortDescription, textRef.current, expandedAboutProduct]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const handleTabChange = (index: any) => {
    setSelectedTabIndex(index);
  };

  const tabData = [
    {
      title: "Protocol",
      text: productTabs.tabsRepeater?.[0]?.tabText,
      icon: <ListIcon w={"16px"} h={"16px"} color={"#718096"} mr={3} />,
    },
    {
      title: "Indications",
      text: productTabs.tabsRepeater?.[1]?.tabText,
      icon: <FlagOutlineIcon w={"16px"} h={"16px"} color={"#718096"} mr={3} />,
    },
    {
      title: "Properties",
      text: productTabs.tabsRepeater?.[2]?.tabText,
      icon: <FlaskIcon w={"16px"} h={"16px"} color={"#718096"} mr={3} />,
    },
    {
      title: "How to use",
      text: productTabs.tabsRepeater?.[3]?.tabText,
      icon: (
        <DocumentTextOutlineIcon
          w={"16px"}
          h={"16px"}
          color={"#718096"}
          mr={3}
        />
      ),
    },
  ];

  const USPs = [
    {
      icon: <UserCircle w="24px" h="24px" fill="#fff" />,
      href: "https://www.medsupplysolutions.com/referral/",
      text: "Referral Program",
    },
    {
      icon: <CurrencyIcon w="24px" h="24px" fill="#fff" />,
      href: "https://www.medsupplysolutions.com/about-us/",
      text: "Price Match",
    },
    {
      icon: <LigthIcon w="24px" h="24px" fill="#fff" />,
      href: "https://www.medsupplysolutions.com/shipping/",
      text: "Fast and Reliable Service",
    },
    {
      icon: <TempIcon w="24px" h="24px" fill="#fff" />,
      href: "https://www.medsupplysolutions.com/shipping/",
      text: "Temperature Condition",
    },
  ];

  // GOOGLE ANALITICS
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_item",
      user_id: loggedIn ? user?.id : "guest",
      ecommerce: {
        items: [
          {
            item_name: name,
            item_id: `product-${databaseId}`,
            price,
            item_brand: brandName[0]?.name || "",
            item_category: catName[0]?.name || "",
            item_category2: catName[1]?.name || "",
            quantity: "1",
          },
        ],
      },
    });
  }, [name, databaseId, price, brandName, categoryName]);

  const trackViewedProduct = () => {
    if (typeof window !== "undefined" && window.klaviyo) {
      const item = {
        ProductName: name,
        ProductID: databaseId,
        Categories: catName.map((category: any) => category.name),
        ImageURL: image?.sourceUrl,
        Brand: brandName,
        Price: price,
      };

      window.klaviyo.push(["track", "Viewed Product", item]);
    } else {
      // eslint-disable-next-line no-console
      console.warn("Klaviyo is not initialized or loaded.");
    }
  };

  React.useEffect(() => {
    trackViewedProduct();
  }, []);

  return (
    <>
      <RegularLayout
        px={4}
        maxWidth="1536px"
        backgroundColor={"#ffff"}
        crumbs={breadcrumbs}
      >
        <>
          <PromoBanner
            loggedIn={loggedIn}
            isUnlicensedUser={isUnlicensedUser}
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify(productSchema, null, 2)}
            </script>
          </Helmet>
          <Stack
            mt={10}
            mx="auto"
            h="auto"
            minH={"684px"}
            alignItems={{ base: "center", xl: "flex-start" }}
            flexDirection={{
              base: "column",
              xl: "row",
            }}
          >
            <>
              <Box flex="1" position="relative">
                <Box width="100%" zIndex={999} position="absolute">
                  {productOnPromotion && (
                    <Box
                      px="6px"
                      bg="#F0FFF4"
                      ml="12px"
                      position="absolute"
                      color="#39A26A"
                    >
                      <Text fontSize="14px" fontWeight="600">
                        Limited Availability
                      </Text>
                    </Box>
                  )}
                  {hasAdditionalPromoTags && (
                    <Box
                      px="6px"
                      bg="#F0FFF4"
                      ml="12px"
                      position="absolute"
                      color="#39A26A"
                      top="24px"
                    >
                      <Text fontSize="14px" fontWeight="600">
                        {hasAdditionalPromoTags}
                      </Text>
                    </Box>
                  )}
                </Box>

                <Carousel
                  images={data.wpSimpleProduct.galleryImages.nodes}
                  order={1}
                />

                <HStack
                  mt={4}
                  flexFlow={{ base: "column", lg: "row" }}
                  bg="#fff"
                  alignItems="flex-start"
                  justifyContent="center"
                  pb={{ base: 10, md: 0 }}
                >
                  <SimpleGrid columns={2} gap="16px">
                    {USPs.map((item, index) => {
                      return (
                        <HStack
                          key={index}
                          gap={{ base: 0.5, md: 0 }}
                          justifyContent="flex-start"
                          w="100%"
                          alignItems="center"
                        >
                          {item.icon}
                          <Divider mx={2.5} orientation="vertical" h="49px" />
                          <Link
                            href={item.href}
                            fontSize="12px"
                            fontWeight="500"
                            color="#171923"
                            textTransform="uppercase"
                          >
                            {item.text}
                          </Link>
                        </HStack>
                      );
                    })}
                  </SimpleGrid>
                </HStack>
              </Box>
            </>

            <SingleProductDetails
              regularPrice={regularPrice}
              salePrice={salePrice}
              brandName={brandName}
              title={name}
              productId={databaseId}
              price={price}
              discount={regularPrice}
              attributesArr={attributes?.attrName}
              tierPrice={tierPrice}
              stockStatus={stockStatus}
              pdf={pdfSlug}
              bundleTitle={bundle.bundleTitle}
              bundleDescription={bundle.bundleDescription}
              bundles={bundles}
              isOnPromotion={productOnPromotion}
            />
          </Stack>
          <Container padding={0} maxW="1280px" py={6} px={{ base: 4, xl: 0 }}>
            <Heading as="h2" fontSize="xl" fontWeight="700">
              About the product
            </Heading>
            <Box
              display="inline-block"
              as="span"
              pb={5}
              maxW="100%"
              ref={textRef}
            >
              <Box
                dangerouslySetInnerHTML={{
                  __html: String(shortDescription),
                }}
                fontSize="lg"
              />
            </Box>

            {isTruncated && (
              <Button
                mt={5}
                fontSize="16px"
                fontWeight="600"
                color="#6A3AAB"
                variant="unstyled"
                onClick={() => setExpandedAboutProduct(!expandedAboutProduct)}
                display="inline-block"
              >
                {expandedAboutProduct ? "Show less" : "Read more"}
              </Button>
            )}
          </Container>

          <Div>
            <Tabs
              display={{ base: "none", lg: "block" }}
              onChange={handleTabChange}
              maxW="1280px"
              px={4}
              py={6}
              mx="auto"
              isFitted
              variant="enclosed"
            >
              <TabList mb="1em" justifyContent={"space-between"}>
                {productTabs && productTabs.tabsRepeater?.length ? (
                  <>
                    <Tab
                      id="protocol"
                      maxW={72}
                      color="#718096"
                      _selected={{
                        background: "#ffffff",
                        color: "#1A212D",
                        border: "1px solid #E2E8F0",
                        borderBottom: "none",
                      }}
                    >
                      <ListIcon
                        color={selectedTabIndex === 0 ? "#CB809E" : "#728197"}
                        mr={2}
                        width="16px"
                        height="16px"
                        fill="#fff"
                      />
                      PROTOCOL
                    </Tab>
                    <Tab
                      id="indications"
                      maxW={72}
                      color="#718096"
                      _selected={{
                        background: "#ffffff",
                        color: "#1A212D",
                        border: "1px solid #E2E8F0",
                        borderBottom: "none",
                      }}
                    >
                      <FlagOutlineIcon
                        color={selectedTabIndex === 1 ? "#CB809E" : "#728197"}
                        mr={2}
                        width="16px"
                        height="16px"
                        fill="#fff"
                      />
                      INDICATIONS
                    </Tab>
                    <Tab
                      id="properties"
                      maxW={72}
                      color="#718096"
                      _selected={{
                        background: "#ffffff",
                        color: "#1A212D",
                        border: "1px solid #E2E8F0",
                        borderBottom: "none",
                      }}
                    >
                      <FlaskIcon
                        fill="#fff"
                        color={selectedTabIndex === 2 ? "#CB809E" : "#728197"}
                        mr={2}
                        width="16px"
                        height="16px"
                      />
                      PROPERTIES
                    </Tab>
                    <Tab
                      id="how"
                      maxW={72}
                      color="#718096"
                      _selected={{
                        background: "#ffffff",
                        color: "#1A212D",
                        border: "1px solid #E2E8F0",
                        borderBottom: "none",
                      }}
                    >
                      <DocumentTextOutlineIcon
                        fill="#fff"
                        color={selectedTabIndex === 3 ? "#CB809E" : "#728197"}
                        mr={2}
                        width="16px"
                        height="16px"
                      />
                      HOW TO USE
                    </Tab>
                  </>
                ) : null}
              </TabList>
              <TabPanels>
                {tabData.map((tab, index) => (
                  <TabPanel py={6} key={index} id={tab.title}>
                    <Box
                      display="inline-block"
                      as="span"
                      fontSize="lg"
                      lineHeight={"150%"}
                      fontWeight={400}
                      maxW={"100%"}
                      pr={4}
                      textAlign="justify"
                    >
                      <Text dangerouslySetInnerHTML={{ __html: tab.text }} />
                    </Box>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>

            <Accordion
              display={{ base: "block", lg: "none" }}
              allowToggle
              bg="#FFF"
              mb={8}
            >
              {productTabs && productTabs.tabsRepeater?.length ? (
                <>
                  {tabData.map((tab, index) => (
                    <AccordionItem key={index}>
                      <AccordionButton fontSize="sm" py={3}>
                        <Box flex="1" textAlign="left">
                          {tab.icon}
                          {tab.title.toUpperCase()}
                        </Box>

                        <AccordionIcon color="#BB5D82" w={"24px"} h={"24px"} />
                      </AccordionButton>
                      <AccordionPanel>
                        <Text dangerouslySetInnerHTML={{ __html: tab.text }} />
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </>
              ) : null}
            </Accordion>
          </Div>
          <Stack px={4} mx="auto" gap={0} maxW="1280px" pb={10}>
            <SubstituteProducts
              substitute={substitute}
              categoryName={categoryName}
              categorySlug={categorySlug}
            />
          </Stack>

          {!bundles || bundles?.length === 0 || !bundles[0]?.bundleName ? (
            <Box bg="#F7FAFC" mx="auto">
              <RelatedProducts
                upsellData={upsellProducts}
                relatedData={relatedProducts}
                title={"Related products"}
              />
            </Box>
          ) : (
            <></>
          )}
        </>
      </RegularLayout>
    </>
  );
};

export default SingleProductPage;

type HeadProps = {
  wpSimpleProduct: {
    seo?: {
      title?: string;
      metaDesc?: string;
    };
  };
};

export const Head: React.FC<HeadProps> = ({ data }: any) => {
  return (
    <SEO
      title={data.wpSimpleProduct.seo?.title}
      description={data.wpSimpleProduct.seo?.metaDesc}
    />
  );
};

export const query = graphql`
  query PRODUCT($slug: String!) {
    wpSimpleProduct(slug: { eq: $slug }) {
      productSubstitute {
        substitute {
          productSku
        }
      }
      crossSellProducts {
        bundleDescription
        bundleTitle
        bundles {
          bundleName
          bundleProduct {
            productSku
          }
        }
      }
      productLeaflet {
        pdf {
          link
          slug
          mediaItemUrl
        }
      }
      productTabs {
        tabsRepeater {
          tabTitle
          tabText
        }
      }
      tierPrice {
        tierPrice {
          price
          price2
          price3
          price4
        }
      }
      isOnPromotion {
        isonpromotion
        additionalTag
      }
      seo {
        title
        fullHead
        metaDesc
        metaKeywords
        schema {
          articleType
          pageType
          raw
        }
        breadcrumbs {
          text
          url
        }
      }
      related {
        relatedProducts: nodes {
          upsell {
            upsellProducts: nodes {
              name
              slug
              databaseId
              description
              date
              productTags {
                nodes {
                  name
                }
              }
              ... on WpSimpleProduct {
                id
                name
                price
                date
                regularPrice
                onSale
                stockStatus
                seo {
                  title
                  metaDesc
                }
              }
            }
          }
          name
          slug
          databaseId
          description
          allPaActiveIngredient {
            nodes {
              name
              slug
              id
            }
          }

          image {
            sourceUrl
            altText
          }
          productTags {
            nodes {
              name
            }
          }
          ... on WpSimpleProduct {
            featuredImage {
              node {
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                  }
                }
                altText
              }
            }
            id
            name
            price
            regularPrice
            date
            onSale
            stockStatus
            tierPrice {
              tierPrice {
                price
                price2
                price3
                price4
              }
            }
          }
        }
      }
      upsell {
        upsellProducts: nodes {
          name
          slug
          databaseId
          description
          allPaActiveIngredient {
            nodes {
              name
              slug
              id
            }
          }
          image {
            sourceUrl
            altText
          }
          productTags {
            nodes {
              name
            }
          }
          ... on WpSimpleProduct {
            featuredImage {
              node {
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                  }
                }
                altText
              }
            }
            id
            name
            price
            regularPrice
            onSale
            stockStatus
            tierPrice {
              tierPrice {
                price
                price2
                price3
                price4
              }
            }
          }
        }
      }
      galleryImages {
        nodes {
          localFile {
            childrenImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
      }
      name
      slug
      databaseId
      shortDescription
      description
      stockStatus
      onSale
      image {
        sourceUrl
        altText
      }
      productCategories {
        catName: nodes {
          name
          slug
          wpParent {
            node {
              id
              name
              slug
            }
          }
        }
      }
      productTags {
        brandName: nodes {
          name
          slug
        }
      }
      attributes {
        attrName: nodes {
          name
          options
        }
      }
      ... on WpSimpleProduct {
        id
        name
        price
        regularPrice
        onSale
        salePrice
      }
    }
  }
`;
